import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ASSET_TYPE_LABELS,
  CONSUMABLE_UNIT_LABELS,
  CONSUMABLE_UNITS,
  roleCanEditCatalog,
  roleCanInventory,
  TOTE_TYPE_LABELS,
  TOTE_TYPES,
  type ConsumableTypeListResponse,
  type ConsumableUnit,
  type ContainerConsumableResponse,
  type PublicConsumableType,
  type PublicContainerConsumable,
  type PublicTote,
  type ToteResponse,
  type ToteType,
} from '@inventohub/shared';
import { ToteExceptionBanner } from '../components/ExceptionBanner';
import { ApiClientError, apiDownload, apiRequest, triggerDownload } from '../lib/api';
import { useAuth } from '../lib/auth';

export function ToteDetailPage({
  qrLanding = false,
  number: numberProp,
}: {
  qrLanding?: boolean;
  number?: string;
}): JSX.Element {
  const { user, code } = useAuth();
  const params = useParams();
  const number = numberProp ?? params.number ?? '';
  const canEdit = user ? roleCanEditCatalog(user.role) : false;
  const canInventory = Boolean(code || (user && roleCanInventory(user.role)));
  const [detail, setDetail] = useState<ToteResponse | null>(null);
  const [types, setTypes] = useState<PublicConsumableType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [qtyType, setQtyType] = useState('');
  const [qty, setQty] = useState('1');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeUnit, setNewTypeUnit] = useState<ConsumableUnit>('each');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const toteBody = await apiRequest<ToteResponse>(`/api/totes/${encodeURIComponent(number)}`);
        let types: PublicConsumableType[] = [];
        if (canEdit) {
          const typeBody = await apiRequest<ConsumableTypeListResponse>('/api/consumable-types');
          types = typeBody.types;
        }
        if (cancelled) return;
        setDetail(toteBody);
        setTypes(types);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load this tote.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [number, canEdit]);

  const tote = detail?.tote;

  async function saveTote(patch: Partial<PublicTote>): Promise<void> {
    if (!tote) return;
    setError(null);
    try {
      const body = await apiRequest<ToteResponse>(`/api/totes/${tote.id}`, {
        method: 'PATCH',
        body: patch,
      });
      setDetail(body);
      setNotice('Saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save the tote.');
    }
  }

  async function addConsumable(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!tote) return;
    setError(null);
    try {
      const body = await apiRequest<ContainerConsumableResponse>(
        `/api/totes/${tote.id}/consumables`,
        {
          method: 'POST',
          body: { consumableTypeId: qtyType, expectedQty: Number(qty) },
        },
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              consumables: [
                ...current.consumables.filter((row) => row.id !== body.consumable.id),
                body.consumable,
              ],
            }
          : current,
      );
      setNotice('Expected quantity saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save the quantity.');
    }
  }

  async function removeConsumable(row: PublicContainerConsumable): Promise<void> {
    if (!tote) return;
    try {
      await apiRequest(`/api/totes/${tote.id}/consumables/${row.id}`, { method: 'DELETE' });
      setDetail((current) =>
        current
          ? { ...current, consumables: current.consumables.filter((entry) => entry.id !== row.id) }
          : current,
      );
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not remove the quantity.',
      );
    }
  }

  async function addType(event: FormEvent): Promise<void> {
    event.preventDefault();
    try {
      const body = await apiRequest<{ type: PublicConsumableType }>('/api/consumable-types', {
        method: 'POST',
        body: { name: newTypeName, unit: newTypeUnit },
      });
      setTypes((current) => [...current, body.type].sort((a, b) => a.name.localeCompare(b.name)));
      setQtyType(body.type.id);
      setNewTypeName('');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not add the type.');
    }
  }

  async function printLabel(): Promise<void> {
    if (!tote) return;
    try {
      const { blob, filename } = await apiDownload('/api/labels', {
        method: 'POST',
        body: { toteId: tote.id },
      });
      triggerDownload(blob, filename);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not print the label.');
    }
  }

  return (
    <section data-testid={qrLanding ? 'qr-landing' : 'tote-detail'}>
      <div className="card">
        <h2>Tote {tote?.number ?? number}</h2>
        {qrLanding ? (
          <p data-testid="qr-signed-in">
            Signed in as {user?.name}. You were not asked to log in again.
          </p>
        ) : null}
        {error ? (
          <p className="status status--error" data-testid="tote-detail-error">
            {error}
          </p>
        ) : null}
        {notice ? <p className="status status--ok">{notice}</p> : null}
        {tote ? (
          <>
            <p className="meta">
              {TOTE_TYPE_LABELS[tote.type]} ·{' '}
              {tote.assignable ? 'Assignable' : 'Retired — cannot be assigned until un-retired'}
            </p>
            {canEdit ? (
              <>
                <label className="field">
                  Type
                  <select
                    value={tote.type}
                    onChange={(event) => void saveTote({ type: event.target.value as ToteType })}
                  >
                    {TOTE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {TOTE_TYPE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="button button--ghost"
                  data-testid="tote-retire"
                  onClick={() =>
                    void saveTote({ status: tote.status === 'retired' ? 'active' : 'retired' })
                  }
                >
                  {tote.status === 'retired' ? 'Un-retire tote' : 'Retire tote'}
                </button>
                <button type="button" className="button" onClick={() => void printLabel()}>
                  Print QR label
                </button>
              </>
            ) : null}
            {canInventory ? (
              <Link
                className="button"
                to={`/inventory/new?tote=${encodeURIComponent(tote.number)}`}
                data-testid="start-inventory"
              >
                Start inventory
              </Link>
            ) : null}
          </>
        ) : null}
      </div>

      {tote && detail ? (
        <ToteExceptionBanner assets={detail.assets} toteNumber={tote.number} />
      ) : null}

      <div className="card">
        <h2>Expected contents</h2>
        <p className="meta">
          Assets whose container is this tote, plus consumable quantities. Official membership
          changes only via a shop move — staff record present / missing / broken / extra during
          inventory and cannot change tote membership.
        </p>
        {detail?.assets.map((asset) => (
          <p key={asset.id}>
            <Link to={`/assets/${encodeURIComponent(asset.serial)}`}>{asset.serial}</Link>
            <span className="meta"> · {ASSET_TYPE_LABELS[asset.type]}</span>
            {asset.status === 'missing' || asset.status === 'broken' ? (
              <span className="status status--warn status--inline"> {asset.status}</span>
            ) : null}
          </p>
        ))}
        {detail && detail.assets.length === 0 ? (
          <p className="meta">No serialized assets in this tote.</p>
        ) : null}
        {detail?.consumables.map((row) => (
          <p key={row.id} data-testid={`consumable-${row.id}`}>
            {row.consumableType.name}: {row.expectedQty}{' '}
            {CONSUMABLE_UNIT_LABELS[row.consumableType.unit]}
            {canEdit ? (
              <>
                {' '}
                <button
                  type="button"
                  className="button button--compact button--ghost"
                  onClick={() => void removeConsumable(row)}
                >
                  Remove
                </button>
              </>
            ) : null}
          </p>
        ))}
      </div>

      {canEdit && tote ? (
        <form className="card" onSubmit={(event) => void addConsumable(event)}>
          <h2>Set consumable quantity</h2>
          <label className="field">
            Type
            <select value={qtyType} onChange={(event) => setQtyType(event.target.value)} required>
              <option value="">Select…</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Expected quantity
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(event) => setQty(event.target.value)}
              required
            />
          </label>
          <button type="submit" className="button">
            Save quantity
          </button>
        </form>
      ) : null}

      {canEdit ? (
        <form className="card" onSubmit={(event) => void addType(event)}>
          <h2>New consumable type</h2>
          <label className="field">
            Name
            <input
              value={newTypeName}
              onChange={(event) => setNewTypeName(event.target.value)}
              required
            />
          </label>
          <label className="field">
            Unit
            <select
              value={newTypeUnit}
              onChange={(event) => setNewTypeUnit(event.target.value as ConsumableUnit)}
            >
              {CONSUMABLE_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {CONSUMABLE_UNIT_LABELS[unit]}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="button button--ghost">
            Add type
          </button>
        </form>
      ) : null}
    </section>
  );
}
